import { useMemo } from "react";
import { ConferenceConfiguration } from "../types/conference.types";
import { ParticipantFeeSummary } from "../types/fee.types";
import { ModuleChangeStatus } from "../components/common/ModuleToggleIndicator";
import {
  useGetConferenceIncludingHiddenQuery,
  useGetConferenceQuery,
} from "../store/conference/conference";
import {
  useListActiveEscortFeesByCongressIdQuery,
  useListEscortFeesByCongressIdQuery,
} from "../store/fee/fee";

export const useModuleChanges = (
  congressId: number,
  registrationConfigJson: ConferenceConfiguration | null,
  isEditMode: boolean,
  participantEscortFee?: ParticipantFeeSummary | null,
) => {
  const { data: activeEvent } = useGetConferenceQuery(congressId, {
    skip: !isEditMode,
  });
  const { data: everUsedModules } = useGetConferenceIncludingHiddenQuery(
    congressId,
    {
      skip: !isEditMode,
    },
  );
  const {
    data: publicEscortFees = [],
    isLoading: isPublicEscortFeesLoading,
  } = useListActiveEscortFeesByCongressIdQuery(congressId, {
    skip: !isEditMode,
  });
  const {
    data: allEscortFees = [],
    isLoading: isAllEscortFeesLoading,
  } = useListEscortFeesByCongressIdQuery(congressId, {
    skip: !isEditMode,
  });

  const moduleChanges = useMemo(() => {
    const activeConfigJson = activeEvent?.configJson; // jelenleg aktív modulok (public endpoint)
    const everUsedConfigJson = everUsedModules?.configJson;

    const unchanged = {
      mailing: "unchanged" as ModuleChangeStatus,
      billing: "unchanged" as ModuleChangeStatus,
      presentation: "unchanged" as ModuleChangeStatus,
      participantFee: "unchanged" as ModuleChangeStatus,
      escortFee: "unchanged" as ModuleChangeStatus,
      accommodation: "unchanged" as ModuleChangeStatus,
      meals: "unchanged" as ModuleChangeStatus,
      activities: "unchanged" as ModuleChangeStatus,
    };

    if (
      !isEditMode ||
      !registrationConfigJson ||
      !activeConfigJson ||
      !everUsedConfigJson
    ) {
      return unchanged;
    }

    if (isPublicEscortFeesLoading || isAllEscortFeesLoading) {
      return unchanged;
    }

    const escortPublicNonEmpty = publicEscortFees.length > 0;
    const escortHiddenNonEmpty = allEscortFees.length > 0;
    const escortCurrentlyActiveForResolve = escortHiddenNonEmpty
      ? activeConfigJson.participantFeeEnabled && escortPublicNonEmpty
      : activeConfigJson.participantFeeEnabled;
    const escortEverUsedForResolve = escortHiddenNonEmpty
      ? true
      : everUsedConfigJson.participantFeeEnabled;

    const participantFeeAtRegistration =
      registrationConfigJson.participantFeeEnabled;
    const escortEffectiveAtRegistration =
      participantEscortFee === undefined
        ? participantFeeAtRegistration
        : participantFeeAtRegistration && participantEscortFee != null;

    const resolveChange = (
      atRegistration: boolean,
      currentlyActive: boolean,
      everUsed: boolean,
    ): ModuleChangeStatus => {
      if (atRegistration === currentlyActive) {
        if (
          everUsed &&
          !(
            atRegistration === currentlyActive &&
            atRegistration === everUsed &&
            currentlyActive === everUsed
          )
        ) {
          return "enabled";
        }
        return "unchanged";
      }
      return currentlyActive ? "enabled" : "disabled";
    };

    return {
      mailing: resolveChange(
        registrationConfigJson.mailingDetailsEnabled,
        activeConfigJson.mailingDetailsEnabled,
        everUsedConfigJson.mailingDetailsEnabled,
      ),
      billing: resolveChange(
        registrationConfigJson.paymentDetailsEnabled,
        activeConfigJson.paymentDetailsEnabled,
        everUsedConfigJson.paymentDetailsEnabled,
      ),
      presentation: resolveChange(
        registrationConfigJson.presentationEnabled,
        activeConfigJson.presentationEnabled,
        everUsedConfigJson.presentationEnabled,
      ),
      participantFee: resolveChange(
        participantFeeAtRegistration,
        activeConfigJson.participantFeeEnabled,
        everUsedConfigJson.participantFeeEnabled,
      ),
      escortFee: resolveChange(
        escortEffectiveAtRegistration,
        escortCurrentlyActiveForResolve,
        escortEverUsedForResolve,
      ),
      accommodation: resolveChange(
        registrationConfigJson.accommodationEnabled,
        activeConfigJson.accommodationEnabled,
        everUsedConfigJson.accommodationEnabled,
      ),
      meals: resolveChange(
        registrationConfigJson.lunchEnabled,
        activeConfigJson.lunchEnabled,
        everUsedConfigJson.lunchEnabled,
      ),
      activities: resolveChange(
        registrationConfigJson.activityEnabled,
        activeConfigJson.activityEnabled,
        everUsedConfigJson.activityEnabled,
      ),
    };
  }, [
    isEditMode,
    registrationConfigJson,
    activeEvent?.configJson,
    everUsedModules?.configJson,
    participantEscortFee,
    publicEscortFees,
    allEscortFees,
    isPublicEscortFeesLoading,
    isAllEscortFeesLoading,
  ]);

  return moduleChanges;
};
